<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Input, Select, Textarea } from '@drama/ui'
import NovelImageField from '../NovelImageField.vue'
import FormField from '../NovelFormField.vue'
import NovelIssueNote from '../NovelIssueNote.vue'
import { newItem } from '@drama/novel-authoring'
import { itemImageTag } from '@drama/novel-authoring'
import { useSectionModel } from './section-model.js'
import './novel-sections.css'

/** Select 只收字符串值：用空串代表"无人持有"，写回时换成 null。 */
const UNHELD = ''

const props = defineProps({
  modelValue: { type: Object, required: true },
  issues: { type: Array, default: () => [] },
  upload: { type: Function, required: true },
})

const emit = defineEmits(['update:modelValue', 'blocked'])
const { t } = useI18n()
const { bind, invalid, mutate, notes, removeEntity, write, error } = useSectionModel(props, emit)
const items = computed(() => props.modelValue.items ?? [])
const holderOptions = computed(() => [
  { value: UNHELD, label: t('novelStudio.fields.unheld') },
  ...(props.modelValue.characters ?? []).map(person => ({ value: person.id, label: person.name || person.id })),
])
const placeOptions = computed(() => (props.modelValue.locations ?? []).map(place => ({ value: place.id, label: place.name || place.id })))

/** 持有人与所在地点存在开场状态 `initial_state.items[物品 id]` 里，不在物品条目上。 */
function itemState(item_id) {
  return props.modelValue.initial_state?.items?.[item_id] ?? {}
}

function setState(item_id, field, value) {
  write(['initial_state', 'items', item_id], { holder_id: null, location_id: '', ...itemState(item_id), [field]: value })
}

/** 物品的错误可能落在条目上，也可能落在它的开场状态上。 */
function itemNotes(item, index) {
  return [...notes(['items', index]), ...notes(['initial_state', 'items', item.id])]
}

/** 新物品连同开场状态一起写入：无人持有，放在第一个地点（Demo 的 add_item）。 */
function addItem() {
  mutate(next => {
    next.items ??= []
    const { item, state } = newItem(next.items, t('novelStudio.fields.newItem'), next)
    next.items.push(item)
    next.initial_state ??= {}
    next.initial_state.items ??= {}
    next.initial_state.items[item.id] = state
  })
}

/** 物品图是可选键：清空即删键。 */
function setImage(index, url) {
  const item = { ...items.value[index] }
  if (url) item.image = url
  else delete item.image
  write(['items', index], item)
}
</script>

<template>
  <section
    class="novel-entities"
    data-testid="novel-items-section"
  >
    <header class="novel-entities__header">
      <p>{{ t('novelStudio.fields.count', { count: items.length }) }}</p>
      <Button
        variant="secondary"
        size="sm"
        data-testid="novel-add-item"
        @click="addItem"
      >
        {{ t('novelStudio.fields.addItem') }}
      </Button>
    </header>
    <p class="novel-entities__hint">
      {{ t('novelStudio.fields.referenceHelp') }}
    </p>
    <p
      v-if="!items.length"
      class="novel-entities__empty"
    >
      {{ t('novelStudio.empty.items') }}
    </p>
    <article
      v-for="(item, index) in items"
      :key="`${item.id}-${index}`"
      class="novel-entity"
      :class="{ 'has-error': invalid(['items', index]) || invalid(['initial_state', 'items', item.id]) }"
      :data-path="`items.${index}`"
      tabindex="-1"
      :data-testid="`novel-item-${index}`"
    >
      <header class="novel-entity__header">
        <div class="novel-entity__title">
          <h3>{{ item.name || item.id }}</h3>
          <code>{{ item.id }}</code>
        </div>
        <Button
          variant="secondary"
          size="sm"
          :aria-label="t('novelStudio.fields.removeNamed', { name: item.name || item.id })"
          :data-testid="`novel-item-${index}-remove`"
          @click="removeEntity('items', index, item.name || item.id)"
        >
          {{ t('novelStudio.fields.remove') }}
        </Button>
      </header>
      <NovelIssueNote :notes="itemNotes(item, index)" />
      <NovelImageField
        :model-value="item.image || ''"
        :label="t('novelStudio.fields.image')"
        :tag="itemImageTag(item.id)"
        :upload="upload"
        :alt="item.name || item.id"
        :invalid="invalid(['items', index, 'image'])"
        :data-path="`items.${index}.image`"
        @update:model-value="setImage(index, $event)"
      />
      <div class="novel-entity__grid">
        <FormField
          :error="error(['items', index, 'name'])"
          required
          :label="t('novelStudio.fields.name')"
        >
          <Input
            v-bind="bind(['items', index, 'name'])"
            :data-testid="`novel-item-${index}-name`"
          />
        </FormField>
        <FormField
          :error="error(['items', index, 'description'])"
          required
          class="is-wide"
          :label="t('novelStudio.fields.description')"
        >
          <Textarea
            v-bind="bind(['items', index, 'description'])"
            :rows="3"
          />
        </FormField>
        <FormField :label="t('novelStudio.fields.holder')">
          <Select
            :model-value="itemState(item.id).holder_id ?? UNHELD"
            :options="holderOptions"
            :aria-label="t('novelStudio.fields.holder')"
            :invalid="invalid(['initial_state', 'items', item.id, 'holder_id'])"
            :data-path="`initial_state.items.${item.id}.holder_id`"
            block
            :data-testid="`novel-item-${index}-holder`"
            @update:model-value="setState(item.id, 'holder_id', $event === UNHELD ? null : $event)"
          />
        </FormField>
        <FormField
          :error="error(['initial_state', 'items', item.id, 'location_id'])"
          required
          :label="t('novelStudio.fields.location')"
        >
          <Select
            :model-value="itemState(item.id).location_id ?? ''"
            :options="placeOptions"
            :aria-label="t('novelStudio.fields.location')"
            :invalid="invalid(['initial_state', 'items', item.id, 'location_id'])"
            :data-path="`initial_state.items.${item.id}.location_id`"
            block
            @update:model-value="setState(item.id, 'location_id', $event)"
          />
        </FormField>
      </div>
    </article>
  </section>
</template>
